import { Col, Row, Space } from 'antd';
import { isNil } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { type StartTestDriveMutation } from '../../../../api';
import { ApplicationStage, type Maybe } from '../../../../api/types';
import NotFoundResult from '../../../../components/results/NotFoundResult';
import UserSessionExpireModal from '../../../../components/UserSessionExpireModal';
import BasicProLayoutContainer from '../../../../layouts/BasicProLayout/BasicProLayoutContainer';
import { useThemeComponents } from '../../../../themes';
import { getApplicationIdentifier } from '../../../../utilities/application';
import useTranslatedString from '../../../../utilities/useTranslatedString';
// eslint-disable-next-line max-len
import useTestDriveStartModal from '../../../shared/ApplicationDetailsPage/generic/TestDriveModal/TestDriveStartedModal';
import FullJourneySteps from '../../../shared/JourneyPage/FullJourneySteps';
import BookingDeposit from '../../../shared/ThankYou/BookingDeposit';
import DealerInfo from '../../../shared/ThankYou/DealerInfo';
import SubmittedContent from '../../../shared/ThankYou/SubmittedContent';
import VehicleDetails from '../../../shared/ThankYou/VehicleDetails';
import { type State } from '../../StandardApplicationEntrypoint/Journey/reducer';
import { type EventApplicationState } from '../Journey/JourneyController';

const colSpan = { lg: 8, md: 12, sm: 24, xs: 24 };

type ContentsTranslation = {
    lead: {
        title: string;
        description: string;
    };
    reservation: {
        title: string;
        description: string;
    };
    emailDescription: string;
    reference: string;
};

export type ThankYouPageProps = {
    state: State<EventApplicationState>;
};

const getEventApplication = (application: EventApplicationState) => {
    if (application.__typename === 'EventApplication') {
        return application;
    }

    throw new Error('Invalid event application');
};

const ThankYouPageContent = ({ state }: ThankYouPageProps) => {
    const { t } = useTranslation('eventThankYou');
    const translatedString = useTranslatedString();
    const navigate = useNavigate();
    const { application } = state;
    const { module } = application;

    const { Button, StandardLayout } = useThemeComponents();

    const eventApplication = getEventApplication(application);

    const hasDeposit = !!application.deposit;

    if (module.__typename !== 'EventApplicationModule') {
        return <NotFoundResult />;
    }
    const onFinishModal = useCallback((data: StartTestDriveMutation) => {
        window.location.href = data?.result?.redirectionLink;
    }, []);
    const [startTestDrive, renderTestDriveModal] = useTestDriveStartModal(
        {
            __typename: 'EventApplication',
            id: application.id,
            vehicle: application.vehicle.__typename === 'LocalVariant' && application.vehicle,
            appointmentStage: application.appointmentStage,
            draftFlow: application.draftFlow,
            moduleId: module.id,
            dealer: application.dealer,
            vehicleId: application.vehicle.__typename === 'LocalVariant' && application.vehicle.id,
        },
        ApplicationStage.Appointment,
        onFinishModal
    );

    const hasTestDriveProcessForPrivateAccess = useMemo(
        () =>
            application.event.privateAccess &&
            !isNil(application.appointmentStage) &&
            application.configuration.testDrive &&
            application.appointmentStage.appointmentModule.hasTestDriveProcess &&
            application.appointmentStage.appointmentModule.hasTestDriveSigning,
        [application.appointmentStage, application.configuration.testDrive, application.event.privateAccess]
    );

    const onDone = useCallback(() => {
        if (hasTestDriveProcessForPrivateAccess) {
            startTestDrive();
        } else {
            navigate(`../${eventApplication.event.urlSlug}`);
        }
    }, [hasTestDriveProcessForPrivateAccess, startTestDrive, navigate, eventApplication.event.urlSlug]);

    const make = useMemo(() => {
        if (application.__typename === 'EventApplication' && application.vehicle.__typename === 'LocalVariant') {
            return translatedString(
                application.vehicle.model.parentModel
                    ? application.vehicle.model.parentModel.make.name
                    : application.vehicle.model.make.name
            );
        }

        return '';
    }, [application, translatedString]);

    const identifier = useMemo(
        () =>
            getApplicationIdentifier(application, [
                ApplicationStage.Financing,
                ApplicationStage.Reservation,
                ApplicationStage.Lead,
            ]),
        [application]
    );

    const contents: ContentsTranslation = t('eventThankYou:contents', {
        returnObjects: true,
        make,
        reference: identifier,
    });
    const contentProps = useMemo(
        () => ({
            title: hasDeposit ? contents.reservation?.title : contents.lead?.title,
            description: hasDeposit ? contents.reservation?.description : contents.lead?.description,
            subDescription: contents.emailDescription,
            reference: contents.reference,
        }),
        [contents, hasDeposit]
    );

    return (
        <div data-cy="eventThankyouPage">
            <StandardLayout title={t('eventThankYou:title')}>
                <BasicProLayoutContainer>
                    <FullJourneySteps stages={state?.stages} />
                    <Row gutter={[24, 24]} justify="center" style={{ marginBottom: '24px' }}>
                        <Col {...colSpan}>
                            <Space direction="vertical" size={24} style={{ width: '100%' }}>
                                <VehicleDetails state={state} />
                                {hasDeposit && <BookingDeposit depositAmount={application.deposit.amount} />}
                            </Space>
                        </Col>
                        <Col {...colSpan}>
                            <Space direction="vertical" size={32} style={{ width: '100%' }}>
                                <SubmittedContent {...contentProps} />
                                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                                    {application?.dealer && application.event.showDealership && (
                                        <DealerInfo dealer={application?.dealer} />
                                    )}
                                    <Button
                                        key="done"
                                        data-cy="eventThankPageButton"
                                        htmlType="button"
                                        onClick={onDone}
                                        type="primary"
                                        block
                                    >
                                        {!hasTestDriveProcessForPrivateAccess
                                            ? t('eventThankYou:actions.done')
                                            : t('eventThankYou:actions.startTestDrive')}
                                    </Button>
                                </Space>
                            </Space>
                        </Col>
                    </Row>
                    {renderTestDriveModal()}
                    {state.application?.event?.privateAccess && <UserSessionExpireModal />}
                </BasicProLayoutContainer>
            </StandardLayout>
        </div>
    );
};

const ThankYouPage = () => {
    const state = useLocation().state as Maybe<State<EventApplicationState>>;

    if (!state) {
        return <NotFoundResult />;
    }

    return <ThankYouPageContent state={state} />;
};

export default ThankYouPage;
